import type { FormInstance } from "../../../../../../../shared/frontend/ui";
import { cssSpacing, spacing, cssPixels, typography, spacing as designSpacing, typography as designTypography } from "../../../app/design-system";
import { getActiveRequestSort } from "../../../app/utils/proTableSortPersistence";
import { DeleteOutlined, DownloadOutlined, EyeOutlined, FilterOutlined, PlusOutlined, PrinterOutlined, SendOutlined, SettingOutlined } from "@ant-design/icons";
import type { ListColumn } from "../../../../../../../shared/frontend/list/listColumn";
import { useQueryClient } from "@tanstack/react-query";
import { App, Button, Flex, Grid, Tag, Tooltip, Typography, ActionMenu } from "../../../../../../../shared/frontend/ui";
import React from "react";
import type { ApiClient } from "../../../app/api/client";
import { CHANNEL_EMAIL_LC,CHANNEL_LETTER_LC } from "../../../app/constants/communicationChannel";
import { PERSON_DOCUMENT_CATEGORY_OUTBOX } from "../../../app/constants/personDocumentCategory";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import type { UnitsSettings } from "../../../app/units/units";
import { CommunicationComposer } from "../../../components/CommunicationComposer";
import { FormDrawer } from "../../../components/FormDrawer";
import { ListDocumentInlay } from "../../../components/ListDocumentInlay";
import { printPersonDocument } from "../../../components/personDocumentPrint";
import { SettingsListPage,type SettingsListActionRef } from "../../../components/SettingsListPage";
import {
createDigitalFileTimelineColumns,
PERSON_DIGITAL_FILE_TIMELINE_COLUMN_DEFINITIONS,
PERSON_DOCUMENT_TIMELINE_STATUS_NO_FILE,
type PersonDigitalFileTimelineRow
} from "./personDigitalFileTimelineUtils";
import { PersonDigitalFileViewer, type PersonDigitalFileSurface } from "./PersonDigitalFileViewer";
import { PersonDocumentDialogs } from "./PersonDocumentDialogs";
import type { PersonDocumentRow } from "./personDetailTypes";
import { personDetailQueryKeys } from "./personDetailQueryKeys";
import type { SendCommunicationOptions } from "./usePersonCommunicationActions";
import type { PersonDto } from "../types";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";

/** Immer sichtbare Aktionen außer den optionalen: Speichern/Menü-Basis. */
const FIXED_ACTION_COUNT = 2;
const MAX_INLINE_ACTION_COUNT = 3;
const DEFAULT_TIMELINE_PAGE_SIZE = 25;
const ACTION_SLOT_COUNT = 3;
const PLUS_MENU_KEY = { letter: "letter", email: "email", upload: "upload" } as const;

type Props = {
  api: ApiClient;
  text: I18nContextValue["text"];
  units: UnitsSettings;
  personId: string;
  person: PersonDto | null;
  allowedStudios: Array<{ id: string }>;
  accountId?: string | null;
  canSendCommunication: boolean;
  canWriteDocuments: boolean;
  filesRef: React.MutableRefObject<SettingsListActionRef | null>;
  timelineDirectionFilter: "" | "in" | "out";
  setTimelineDirectionFilter: (value: "" | "in" | "out") => void;
  timelineTypeFilter: Set<string>;
  setTimelineTypeFilter: React.Dispatch<React.SetStateAction<Set<string>>>;
  communicationMode: "list" | "create";
  communicationSaving: boolean;
  communicationForm: FormInstance;
  openCommunication: (channelOverride?: "email" | "letter") => void;
  closeCommunication: () => void;
  sendCommunication: (options?: SendCommunicationOptions) => Promise<void>;
};

const DOCUMENT_UPLOAD_MAX_BYTES = 200_000_000;
const DOCUMENT_UPLOAD_ALLOWED_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];
const DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".pdf", ".txt", ".csv", ".doc", ".docx"];
const HIDDEN_TIMELINE_ACTION_STYLE: React.CSSProperties = { visibility: "hidden", pointerEvents: "none" };

type CommunicationHeaderActionState = {
  submitText: string;
  disabled: boolean;
  printVisible: boolean;
  sendVisible: boolean;
};

type CommunicationCreateHeaderBaseProps = {
  communicationSaving: boolean;
  text: I18nContextValue["text"];
  title?: string;
  onOpenSettings?: () => void;
  closeCommunication: () => void;
  sendCommunication: (options?: SendCommunicationOptions) => Promise<void>;
  onPrintDocument?: () => void;
  onSaveDocument?: () => void;
};

type CommunicationCreateHeaderProps = CommunicationCreateHeaderBaseProps & {
  communicationValues: Record<string, unknown>;
  personEmail?: string | null;
};

function buildCommunicationHeaderActionState({
  channelValue,
  sendEmailValue,
  sendPrintValue,
  sendApiValue,
  personEmail,
  text
}: {
  channelValue: unknown;
  sendEmailValue: unknown;
  sendPrintValue: unknown;
  sendApiValue: unknown;
  personEmail?: string | null;
  text: I18nContextValue["text"];
}): CommunicationHeaderActionState {
  const channel = Array.isArray(channelValue)
    ? channelValue[0]
    : channelValue;
  const isLetter = String(channel || "").toLowerCase() === CHANNEL_LETTER_LC;
  const isEmail = String(channel || "").toLowerCase() === CHANNEL_EMAIL_LC;
  const sendEmail = sendEmailValue === true && !!personEmail;
  const sendPrint = sendPrintValue === true;
  const sendApi = sendApiValue === true;
  const hasSendChannel = sendEmail || sendApi;
  const sendVisible = isEmail || (isLetter && hasSendChannel);
  return {
    submitText: isLetter && !hasSendChannel ? text("Save") : text("Send"),
    disabled: isEmail && !personEmail,
    printVisible: isLetter && sendPrint,
    sendVisible
  };
}

function CommunicationCreateHeaderLayout({
  actionState,
  communicationSaving,
  text,
  title,
  onOpenSettings,
  closeCommunication,
  sendCommunication,
  onPrintDocument,
  onSaveDocument
}: CommunicationCreateHeaderBaseProps & {
  actionState: CommunicationHeaderActionState;
}) {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  // Höchstens drei Knöpfe einschließlich „abbrechen“ und Hauptknopf: was darüber hinausgeht
  // (zuerst Einstellungen, dann Drucken), steht im Menü dahinter.
  const showSave = Boolean(onSaveDocument && actionState.sendVisible);
  const optionalActions = [
    onOpenSettings ? { key: "settings", label: text("Settings"), icon: <SettingOutlined />, onClick: onOpenSettings } : null,
    actionState.printVisible && onPrintDocument ? { key: "print", label: text("Print"), icon: <PrinterOutlined />, onClick: onPrintDocument } : null
  ].filter((action): action is NonNullable<typeof action> => action !== null);
  const menuCount = Math.max(0, optionalActions.length + (showSave ? 1 : 0) + FIXED_ACTION_COUNT - MAX_INLINE_ACTION_COUNT);
  const menuActions = optionalActions.slice(0, menuCount);
  const secondaryActions = optionalActions.slice(menuCount);

  return (
    <Flex
      justify="space-between"
      align={isMobile ? "stretch" : "center"}
      gap={12}
      vertical={isMobile}
      wrap={isMobile ? false : "wrap"}
      style={{ marginBottom: spacing.xl }}
    >
      <Typography.Title level={4} style={{ margin: 0 }}>
        {title || text("Send letter/email")}
      </Typography.Title>
      <Flex
        justify="flex-end"
        align="center"
        gap={8}
        wrap="wrap"
        style={{ marginLeft: isMobile ? 0 : "auto", minWidth: 0, width: isMobile ? "100%" : undefined }}
      >
        {secondaryActions.map((action) => (
          <Button key={action.key} icon={action.icon} onClick={action.onClick}>
            {action.label}
          </Button>
        ))}
        {menuActions.length > 0 ? (
          <ActionMenu
            items={menuActions.map(({ key, label, icon }) => ({ key, label, icon }))}
            onAction={(key) => menuActions.find((action) => action.key === key)?.onClick()}
          />
        ) : null}
        {showSave ? (
          <Button loading={communicationSaving} onClick={onSaveDocument}>
            {text("Save")}
          </Button>
        ) : null}
        <Button onClick={closeCommunication}>{text("Cancel")}</Button>
        <Button
          type="primary"
          loading={communicationSaving}
          disabled={actionState.disabled}
          onClick={() => {
            if (onSaveDocument && !actionState.sendVisible) {
              onSaveDocument();
              return;
            }
            return sendCommunication().catch(() => {});
          }}
        >
          {actionState.submitText}
        </Button>
      </Flex>
    </Flex>
  );
}

function CommunicationCreateHeader(props: CommunicationCreateHeaderProps) {
  const actionState = buildCommunicationHeaderActionState({
    channelValue: props.communicationValues.channel,
    sendEmailValue: props.communicationValues.sendEmail,
    sendPrintValue: props.communicationValues.sendPrint,
    sendApiValue: props.communicationValues.sendApi,
    personEmail: props.personEmail,
    text: props.text
  });
  return <CommunicationCreateHeaderLayout {...props} actionState={actionState} />;
}

function TimelineFilterGroup({
  label,
  isMobile,
  children
}: {
  label: React.ReactNode;
  isMobile: boolean;
  children: React.ReactNode;
}) {
  return (
    <Flex
      vertical={isMobile}
      gap={isMobile ? 6 : 8}
      align={isMobile ? "stretch" : "center"}
      style={{ minWidth: 0, width: isMobile ? "100%" : undefined }}
    >
      <Typography.Text type="secondary" style={{ fontSize: designTypography.fontSize.base, flexShrink: 0 }}>
        {label}
      </Typography.Text>
      {isMobile ? (
        <div
          className="person-digital-file-filter-options"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: designSpacing.controlCompact,
            minWidth: 0,
            width: "100%"
          }}
        >
          {children}
        </div>
      ) : (
        <Flex wrap="wrap" gap={6} align="center" style={{ minWidth: 0 }}>
          {children}
        </Flex>
      )}
    </Flex>
  );
}

export function PersonDigitalFileTab({
  api,
  text,
  units,
  personId,
  person,
  allowedStudios,
  accountId,
  canSendCommunication,
  canWriteDocuments,
  filesRef,
  timelineDirectionFilter,
  setTimelineDirectionFilter,
  timelineTypeFilter,
  setTimelineTypeFilter,
  communicationMode,
  communicationSaving,
  communicationForm,
  openCommunication,
  closeCommunication,
  sendCommunication
}: Props) {
  const queryClient = useQueryClient();
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const useStackedTimelineFilters = screens.md === false;
  const timelineAllColumnsMap: Record<string, ListColumn<PersonDigitalFileTimelineRow>> = React.useMemo(
    () => createDigitalFileTimelineColumns(text, units),
    [text, units]
  );
  const fileUploadInputRef = React.useRef<HTMLInputElement>(null);
  const [fileUploading, setFileUploading] = React.useState(false);
  const [viewerRow, setViewerRow] = React.useState<PersonDigitalFileTimelineRow | null>(null);
  const [viewerDoc, setViewerDoc] = React.useState<PersonDocumentRow | null>(null);
  const [viewerSurface, setViewerSurface] = React.useState<PersonDigitalFileSurface>("document");
  const [resending, setResending] = React.useState(false);
  const [resendChannelOpen, setResendChannelOpen] = React.useState(false);
  const [resendChannel, setResendChannel] = React.useState<"email" | "brief24">("email");
  const [uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = React.useState("");
  const [uploadDocDescription, setUploadDocDescription] = React.useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const communicationComposerTopRef = React.useRef<HTMLDivElement | null>(null);
  const [communicationHeaderValues, setCommunicationHeaderValues] = React.useState<Record<string, unknown>>({});

  React.useEffect(() => {
    const handleDocumentsChanged = (event: CustomEvent<{ personId: string }>) => {
      if (event.detail?.personId === personId) void filesRef.current?.revalidateCurrentQuery();
    };
    window.addEventListener("person-documents-changed", handleDocumentsChanged as EventListener);
    return () => window.removeEventListener("person-documents-changed", handleDocumentsChanged as EventListener);
  }, [filesRef, personId]);

  React.useEffect(() => {
    if (communicationMode !== "create") return;
    const scrollTimer = window.setTimeout(() => {
      setCommunicationHeaderValues(communicationForm.getFieldsValue(true));
      communicationComposerTopRef.current?.scrollIntoView({ block: "start", inline: "nearest" });
    }, 0);
    return () => window.clearTimeout(scrollTimer);
  }, [communicationForm, communicationMode]);

  const handleFileSelect = React.useCallback((file: File) => {
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
    const isValidType = DOCUMENT_UPLOAD_ALLOWED_TYPES.includes(file.type) || DOCUMENT_UPLOAD_ALLOWED_EXTENSIONS.includes(fileExt);
    if (!isValidType) {
      feedback.error(text("Invalid file type. Allowed: Images (JPG, PNG, GIF, WEBP), PDF, Text (TXT, CSV), Word (DOC, DOCX)"));
      return;
    }
    if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
      feedback.error(text("File too large (max 200MB)"));
      return;
    }
    setUploadFile(file);
    setUploadTitle("");
    setUploadDocDescription("");
    setUploadDialogOpen(true);
  }, [feedback, text]);

  const handleFileUpload = React.useCallback(async () => {
    if (!uploadFile) return;

    setFileUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      const uploadParams = new URLSearchParams();
      const title = uploadTitle.trim();
      const description = uploadDocDescription.trim();
      if (title) uploadParams.set("title", title);
      if (description) uploadParams.set("description", description);
      const uploadQuery = uploadParams.toString();
      await api.post(
        `/api/v1/persons/${encodeURIComponent(personId)}/documents${uploadQuery ? `?${uploadQuery}` : ""}`,
        formData
      );
      feedback.success(text("Document uploaded."));
      void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.documents(personId) });
      void filesRef.current?.revalidateCurrentQuery();
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadTitle("");
      setUploadDocDescription("");
    } finally {
      setFileUploading(false);
      if (fileUploadInputRef.current) fileUploadInputRef.current.value = "";
    }
  }, [api, filesRef, feedback, personId, queryClient, text, uploadDocDescription, uploadFile, uploadTitle]);

  const openDocumentFromId = React.useCallback(async (docId: string) => {
    const list = await api.get<PersonDocumentRow[]>(`/api/v1/persons/${encodeURIComponent(personId)}/documents`);
    queryClient.setQueryData(personDetailQueryKeys.person.documents(personId), list || []);
    const match = (list || []).find((document: PersonDocumentRow) => document.id === docId) || null;
    if (!match) return null;
    // Einträge ohne hinterlegte Datei stammen aus Altdaten-Importen: der Vorgang ist belegt, es
    // gibt aber nichts anzuzeigen. Der Viewer würde nur eine leere Vorschau öffnen.
    if (match.hasFile === false) {
      feedback.info(text("Document file not available"));
      return null;
    }
    return match;
  }, [api, feedback, personId, queryClient, text]);

  const openTimelineRow = React.useCallback(async (row: PersonDigitalFileTimelineRow) => {
    setViewerSurface(row.source === "email" ? "email" : "document");
    if (row.source === "document" && row.personDocumentId) {
      const match = await openDocumentFromId(row.personDocumentId);
      if (!match) return;
      setViewerDoc(match);
      setViewerRow(row);
      return;
    }
    if (row.source === "email" && row.emailQueueId) {
      setViewerDoc(null);
      setViewerRow(row);
      return;
    }
    if (row.source === "reminder" && row.reminderId) {
      setViewerDoc(null);
      setViewerRow(row);
    }
  }, [openDocumentFromId]);

  const requestTimeline = React.useCallback(async (
    params: { current?: number; pageSize?: number },
    sort?: Record<string, "ascend" | "descend">
  ) => {
    const url = new URL(`/api/v1/persons/${encodeURIComponent(personId)}/timeline`, window.location.origin);
    if (timelineDirectionFilter) url.searchParams.set("direction", timelineDirectionFilter);
    if (timelineTypeFilter.size > 0) url.searchParams.set("types", Array.from(timelineTypeFilter).join(","));
    const page = Number(params?.current) > 0 ? Number(params.current) : 1;
    const pageSize = Number(params?.pageSize) > 0 ? Number(params.pageSize) : DEFAULT_TIMELINE_PAGE_SIZE;
    url.searchParams.set("page", String(page));
    url.searchParams.set("pageSize", String(pageSize));
    const activeSort = getActiveRequestSort(sort);
    if (activeSort) {
      url.searchParams.set("sort", `${activeSort.field}:${activeSort.direction}`);
    }
    try {
      const res = await api.get<{ total: number; data: PersonDigitalFileTimelineRow[] }>(url.pathname + url.search);
      return { data: res?.data || [], success: true, total: res?.total || 0 };
    } catch {
      return { data: [], success: false, total: 0 };
    }
  }, [api, personId, timelineDirectionFilter, timelineTypeFilter]);

  const timelineActionsColumn: ListColumn<PersonDigitalFileTimelineRow> & { slotCount?: number } = React.useMemo(
    () => ({
      title: text("Actions"),
      key: "actions",
      valueType: "option",
      slotCount: ACTION_SLOT_COUNT,
      fixed: "right",
      align: "right",
      render: (_: unknown, row: PersonDigitalFileTimelineRow) => [
        // Ein Dokument ohne hinterlegte Datei hat nichts zu öffnen. Der Platz bleibt als
        // unsichtbarer Knopf stehen, damit die Aktionsspalte über alle Zeilen gleich breit bleibt.
        String(row.status || "").toLowerCase() === PERSON_DOCUMENT_TIMELINE_STATUS_NO_FILE ? (
          <Button
            key="open"
            type="link"
            size="small"
            disabled
            icon={<EyeOutlined />}
            style={HIDDEN_TIMELINE_ACTION_STYLE}
            tabIndex={-1}
            aria-hidden
          >
            {text("Open")}
          </Button>
        ) : (
          <Button key="open" type="link" size="small" icon={<EyeOutlined />} onClick={() => { return openTimelineRow(row).catch(() => {}); }}>
            {text("Open")}
          </Button>
        ),
        row.attachmentDocumentId ? (
          <Button
            key="download"
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => window.open(`/api/v1/persons/${encodeURIComponent(personId)}/documents/${encodeURIComponent(row.attachmentDocumentId!)}`, "_blank", "noopener,noreferrer")}
          >
            {text("Download")}
          </Button>
        ) : (
          <Button
            key="download"
            type="link"
            size="small"
            disabled
            icon={<DownloadOutlined />}
            style={HIDDEN_TIMELINE_ACTION_STYLE}
            tabIndex={-1}
            aria-hidden
          >
            {text("Download")}
          </Button>
        ),
        canWriteDocuments && row.source === "document" && row.personDocumentId ? (
          <Button
            key="delete"
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              return modal.confirm({
                title: text("Delete document?"),
                content: text("Delete document confirm").replace("{0}", row.title || ""),
                okText: text("Delete"),
                okType: "danger",
                cancelText: text("Cancel"),
                onOk: async () => {
                  await api.del(`/api/v1/persons/${encodeURIComponent(personId)}/documents/${encodeURIComponent(row.personDocumentId!)}`);
                  feedback.success(text("Document deleted."));
                  void queryClient.invalidateQueries({ queryKey: personDetailQueryKeys.person.documents(personId) });
                  filesRef.current?.reload?.();
                }
              });
            }}
          >
            {text("Delete")}
          </Button>
        ) : (
          <Button
            key="delete"
            type="link"
            size="small"
            danger
            disabled
            icon={<DeleteOutlined />}
            style={HIDDEN_TIMELINE_ACTION_STYLE}
            tabIndex={-1}
            aria-hidden
          >
            {text("Delete")}
          </Button>
        ),
      ]
    }),
    [api, canWriteDocuments, filesRef, feedback, modal, openTimelineRow, personId, queryClient, text]
  );

  const plusMenuItems = React.useMemo(() => {
    const items: Array<{ key: string; label: string }> = [];
    if (canSendCommunication) {
      items.push({ key: PLUS_MENU_KEY.letter, label: text("Write letter") });
      items.push({ key: PLUS_MENU_KEY.email, label: text("Send email") });
    }
    if (canWriteDocuments) items.push({ key: PLUS_MENU_KEY.upload, label: text("Upload document") });
    return items;
  }, [canSendCommunication, canWriteDocuments, text]);

  const onPlusMenuClick = React.useCallback((key: string) => {
    if (key === PLUS_MENU_KEY.letter) openCommunication("letter");
    else if (key === PLUS_MENU_KEY.email) openCommunication("email");
    else if (key === PLUS_MENU_KEY.upload) fileUploadInputRef.current?.click();
  }, [openCommunication]);

  const closeViewer = React.useCallback(() => {
    setViewerRow(null);
    setViewerDoc(null);
  }, []);

  const onViewerDocumentResolved = React.useCallback((doc: PersonDocumentRow | null) => {
    setViewerDoc(doc);
  }, []);

  const closeUploadDialog = React.useCallback(() => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setUploadTitle("");
    setUploadDocDescription("");
    if (fileUploadInputRef.current) fileUploadInputRef.current.value = "";
  }, []);

  if (viewerRow) {
    return (
      <>
        <ListDocumentInlay
          title={viewerRow.title || viewerDoc?.title || text("Document")}
          text={text}
          onBack={closeViewer}
          onPrint={viewerDoc && viewerSurface === "document" ? () => {
            void printPersonDocument({
              personId,
              documentId: viewerDoc.id,
              loadDocumentBlob: (url) => api.blob(url),
              preparingText: text("Preparing print..."),
              documentTitle: viewerDoc.title || viewerDoc.fileName || text("Document"),
              onPrintFailed: () => feedback.error(text("Print failed."))
            });
          } : undefined}
          onDownload={viewerDoc && viewerSurface === "document" ? () => window.open(`/api/v1/persons/${encodeURIComponent(personId)}/documents/${encodeURIComponent(viewerDoc.id)}`, "_blank", "noopener,noreferrer") : undefined}
          closeOnEscape={!uploadDialogOpen && !resendChannelOpen}
          contentOverflow="hidden"
          extra={(
            <>
              {viewerSurface === "document" && viewerDoc?.category === PERSON_DOCUMENT_CATEGORY_OUTBOX && canWriteDocuments && (
                <Tooltip title={isMobile ? text("Resend letter") : undefined}>
                  <Button
                    icon={<SendOutlined />}
                    loading={resending}
                    onClick={() => setResendChannelOpen(true)}
                    aria-label={isMobile ? text("Resend letter") : undefined}
                    title={isMobile ? text("Resend letter") : undefined}
                  >
                    {isMobile ? null : text("Resend letter")}
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        >
          <PersonDigitalFileViewer
            key={viewerRow.id}
            api={api}
            personId={personId}
            text={text}
            units={units}
            row={viewerRow}
            document={viewerDoc}
            surface={viewerSurface}
            onSurfaceChange={setViewerSurface}
            onDocumentResolved={onViewerDocumentResolved}
          />
        </ListDocumentInlay>
        <PersonDocumentDialogs
          text={text}
          uploadOpen={uploadDialogOpen}
          uploadFile={uploadFile}
          uploadTitle={uploadTitle}
          uploadDescription={uploadDocDescription}
          uploading={fileUploading}
          onUploadTitleChange={setUploadTitle}
          onUploadDescriptionChange={setUploadDocDescription}
          onUploadCancel={closeUploadDialog}
          onUploadSubmit={handleFileUpload}
          resendOpen={resendChannelOpen}
          resendChannel={resendChannel}
          resending={resending}
          viewerDoc={viewerDoc}
          onResendChannelChange={setResendChannel}
          onResendCancel={() => setResendChannelOpen(false)}
          onResendSubmit={async () => {
            if (!viewerDoc) return;
            setResendChannelOpen(false);
            setResending(true);
            try {
              const result = await api.post<{ success: boolean; message: string; isPdf?: boolean }>(`/api/v1/persons/${encodeURIComponent(personId)}/documents/${encodeURIComponent(viewerDoc.id)}/resend`, {});
              feedback.success(result.message || text("Document is being resent."));
            } finally {
              setResending(false);
            }
          }}
        />
      </>
    );
  }

  if (communicationMode === "create") {
    return (
      <div
        ref={communicationComposerTopRef}
        className="person-digital-file-communication-composer"
        style={{ scrollMarginTop: isMobile ? 156 : 132 }}
      >
        <CommunicationCreateHeader
          communicationValues={communicationHeaderValues}
          communicationSaving={communicationSaving}
          personEmail={person?.email}
          text={text}
          closeCommunication={closeCommunication}
          sendCommunication={sendCommunication}
        />
        <CommunicationComposer
          form={communicationForm}
          mode="send"
          showTemplateSelection
          showStudioSelection
          showChannelSelection
          showSendingOptions={true}
          showSubject
          showDocumentTitle
          studioMode="single"
          channelMode="single"
          allowedChannels={["email", "letter"]}
          personEmail={person?.email}
          initialStudioId={person?.homeStudioId || allowedStudios[0]?.id || null}
          showSettingsInline
          onValuesChange={(_, allValues) => setCommunicationHeaderValues(allValues)}
        />
      </div>
    );
  }

  const timelineFilters = (
    <Flex
      className="person-digital-file-timeline-filters"
      vertical={useStackedTimelineFilters}
      gap={useStackedTimelineFilters ? 12 : 18}
      align={useStackedTimelineFilters ? "stretch" : "center"}
      wrap={useStackedTimelineFilters ? false : "wrap"}
      style={{ width: useStackedTimelineFilters ? "100%" : undefined, minWidth: 0 }}
    >
      <TimelineFilterGroup label={`${text("Direction")}:`} isMobile={useStackedTimelineFilters}>
        {[
          { value: "", label: text("All") },
          { value: "in", label: text("Incoming") },
          { value: "out", label: text("Outgoing") }
        ].map((option) => (
          <Tag.CheckableTag
            key={option.value || "all"}
            checked={timelineDirectionFilter === option.value}
            onChange={() => {
              setTimelineDirectionFilter(option.value as "" | "in" | "out");
              filesRef.current?.reload?.();
            }}
            style={{ fontSize: designTypography.fontSize.base, padding: cssSpacing(spacing.controlCompact, spacing.relaxed), lineHeight: cssPixels(typography.fontSize.lg), marginInlineEnd: 0, textAlign: "center" }}
          >
            {option.label}
          </Tag.CheckableTag>
        ))}
      </TimelineFilterGroup>
      <TimelineFilterGroup label={`${text("Type")}:`} isMobile={useStackedTimelineFilters}>
        {[
          { value: "letter", label: text("Letter") },
          { value: "email", label: text("Email") },
          { value: "reminder", label: text("Reminder") },
          { value: "contract", label: text("Contract") },
          { value: "upload", label: text("File") }
        ].map((option) => (
          <Tag.CheckableTag
            key={option.value}
            checked={timelineTypeFilter.has(option.value)}
            onChange={(checked) => {
              setTimelineTypeFilter((prev) => {
                const next = new Set(prev);
                if (checked) next.add(option.value);
                else next.delete(option.value);
                return next;
              });
              filesRef.current?.reload?.();
            }}
            style={{ fontSize: designTypography.fontSize.base, padding: cssSpacing(spacing.controlCompact, spacing.relaxed), lineHeight: cssPixels(typography.fontSize.lg), marginInlineEnd: 0, textAlign: "center" }}
          >
            {option.label}
          </Tag.CheckableTag>
        ))}
      </TimelineFilterGroup>
    </Flex>
  );
  const newDocumentButton = plusMenuItems.length > 0 ? (
    <ActionMenu type="primary" icon={<PlusOutlined />} loading={fileUploading} label={text("New")}
      items={plusMenuItems.map((item) => ({ key: item.key, label: item.label }))} onAction={onPlusMenuClick} />
  ) : null;
  const mobileTimelineActions = useStackedTimelineFilters ? (
    <Flex
      className="person-digital-file-mobile-actions"
      wrap="wrap"
      gap={8}
      justify="flex-end"
      align="center"
      style={{ width: "100%", minWidth: 0 }}
    >
      <Button icon={<FilterOutlined />} onClick={() => setFilterDrawerOpen(true)}>
        {text("Filter")}
      </Button>
      {newDocumentButton}
    </Flex>
  ) : null;

  return (
    <>
      <SettingsListPage<PersonDigitalFileTimelineRow>
        dataMode="server-query"
        toolbarExtra={useStackedTimelineFilters ? undefined : timelineFilters}
        toolbarRightExtra={useStackedTimelineFilters ? mobileTimelineActions : newDocumentButton}
        storageKey="persons.detail.timeline"
        columnEditorStorageKey={`persons-detail-timeline-columns-${accountId || "default"}`}
        rowKey="id"
        request={requestTimeline}
        columnDefinitions={PERSON_DIGITAL_FILE_TIMELINE_COLUMN_DEFINITIONS}
        allColumnsMap={timelineAllColumnsMap}
        actionsColumn={timelineActionsColumn}
        toolBarActions={[]}
        onRowClick={(row) => { openTimelineRow(row).catch(() => {}); }}
        actionRef={filesRef}
        pagination={{ pageSize: 25 }}
      />
      <FormDrawer dirty={false} hideCancel
        title="Filter"
        open={filterDrawerOpen}
        onCancel={() => setFilterDrawerOpen(false)}
        hideSubmit
        cancelText="Close"
        autoFocusFirstField={false}
      >
        {timelineFilters}
      </FormDrawer>
      <input
        ref={fileUploadInputRef}
        type="file"
        style={{ display: "none" }}
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.csv,.doc,.docx,image/*,application/pdf,text/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) handleFileSelect(file);
        }}
      />
      <PersonDocumentDialogs
        text={text}
        uploadOpen={uploadDialogOpen}
        uploadFile={uploadFile}
        uploadTitle={uploadTitle}
        uploadDescription={uploadDocDescription}
        uploading={fileUploading}
        onUploadTitleChange={setUploadTitle}
        onUploadDescriptionChange={setUploadDocDescription}
        onUploadCancel={closeUploadDialog}
        onUploadSubmit={handleFileUpload}
        resendOpen={resendChannelOpen}
        resendChannel={resendChannel}
        resending={resending}
        viewerDoc={viewerDoc}
        onResendChannelChange={setResendChannel}
        onResendCancel={() => setResendChannelOpen(false)}
        onResendSubmit={async () => {}}
      />
    </>
  );
}
